import { Circle, Replay } from '@mui/icons-material';
import { Box, CircularProgress, Typography } from '@mui/material';
import { LightTooltip } from 'lib/designSystem/WhiteTooltip';
import React from 'react';

export interface CessionStatusData {
  isAccepted?: boolean;
  isProcessing?: boolean;
  isRejected?: boolean;
  isReceding?: boolean;
  isQuerySuccessful?: boolean;
  isLoading?: boolean;
  canRetry?: boolean;
  onRecede?: () => void;
  UrlAec?: string;
}

interface CessionStatusLightProps {
  status?: CessionStatusData;
}

const CessionStatusLight: React.FC<CessionStatusLightProps> = ({ status }) => {
  if (!status || typeof status !== 'object') {
    return (
      <LightTooltip
        placement="top"
        title={
          <Typography sx={{ padding: 1 }}>Sin información de cesión</Typography>
        }
      >
        <Circle
          sx={{
            color: '#9E9E9E',
            fontWeight: 'bold',
            fontSize: '1.2rem',
          }}
        />
      </LightTooltip>
    );
  }

  if (status.isLoading === true) {
    return (
      <LightTooltip
        placement="top"
        title={
          <Typography sx={{ padding: 1 }}>
            Consultando estado de cesión con el SII...
          </Typography>
        }
      >
        <CircularProgress
          size={20}
          sx={{
            color: '#9E9E9E',
          }}
        />
      </LightTooltip>
    );
  }

  if (status.isAccepted === true) {
    const handleClick = () => {
      if (status.UrlAec) {
        window.open(status.UrlAec, '_blank', 'noopener,noreferrer');
      }
    };

    return (
      <LightTooltip
        placement="top"
        title={
          <Typography sx={{ padding: 1 }}>
            Cesión aceptada por el SII
          </Typography>
        }
      >
        <Box
          onClick={status.UrlAec ? handleClick : undefined}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: status.UrlAec ? 'pointer' : 'default',
            transition: 'all 0.3s ease',
            '&:hover': status.UrlAec
              ? {
                  transform: 'scale(1.2)',
                }
              : {},
          }}
        >
          <Circle
            sx={{
              color: '#3bc14a',
              fontWeight: 'bold',
              fontSize: '1.2rem',
            }}
          />
        </Box>
      </LightTooltip>
    );
  }

  if (status.isProcessing === true) {
    return (
      <LightTooltip
        placement="top"
        title={
          <Typography sx={{ padding: 1 }}>
            Cesión en proceso de validación por el SII
          </Typography>
        }
      >
        <Circle
          sx={{
            color: '#E8C547',
            fontWeight: 'bold',
            fontSize: '1.2rem',
          }}
        />
      </LightTooltip>
    );
  }

  if (status.isRejected === true) {
    const isReceding = status.isReceding === true;
    const canRetry = status.canRetry === true;

    return (
      <LightTooltip
        placement="top"
        title={
          <Typography sx={{ padding: 1 }}>
            {isReceding
              ? 'Procesando re-cesión...'
              : canRetry
                ? 'Cesión rechazada por el SII. Haz clic para reintentar.'
                : 'Cesión rechazada por el SII'}
          </Typography>
        }
      >
        <Box
          onClick={() => !isReceding && canRetry && status.onRecede?.()}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: isReceding
              ? 'not-allowed'
              : canRetry
                ? 'pointer'
                : 'default',
            position: 'relative',
            transition: 'all 0.3s ease',
            opacity: isReceding ? 0.6 : 1,
            '&:hover': {
              transform: isReceding ? 'none' : canRetry ? 'scale(1.3)' : 'none',
            },
            '&:hover .circle-icon': {
              opacity: isReceding ? 1 : canRetry ? 0 : 1,
            },
            '&:hover .replay-icon': {
              opacity: isReceding ? 0 : canRetry ? 1 : 0,
            },
          }}
        >
          {isReceding ? (
            <CircularProgress
              size={20}
              sx={{
                color: '#D62839',
              }}
            />
          ) : (
            <>
              <Circle
                className="circle-icon"
                sx={{
                  color: '#D62839',
                  fontWeight: 'bold',
                  fontSize: '1.2rem',
                  transition: 'opacity 0.3s ease',
                }}
              />
              {canRetry && (
                <Replay
                  className="replay-icon"
                  sx={{
                    position: 'absolute',
                    color: '#D62839',
                    opacity: 0,
                    transition: 'opacity 0.3s ease',
                    fontSize: '1.2rem',
                  }}
                />
              )}
            </>
          )}
        </Box>
      </LightTooltip>
    );
  }

  if (status.isQuerySuccessful === false) {
    return (
      <LightTooltip
        placement="top"
        title={
          <Typography sx={{ padding: 1 }}>
            Hubo un error al consultar el estado de la cesión con el SII
          </Typography>
        }
      >
        <Circle
          sx={{
            color: '#FF9800',
            fontWeight: 'bold',
            fontSize: '1.2rem',
          }}
        />
      </LightTooltip>
    );
  }

  return (
    <LightTooltip
      placement="top"
      title={
        <Typography sx={{ padding: 1 }}>
          No se pudo consultar el estado de la cesión con el SII.
        </Typography>
      }
    >
      <Circle
        sx={{
          color: '#9E9E9E',
          fontWeight: 'bold',
          fontSize: '1.2rem',
        }}
      />
    </LightTooltip>
  );
};

export default CessionStatusLight;
